import { useMicroFrontContext } from './micro-front/MicroFrontContext';
import MicroFront from './micro-front/MicroFront';
import { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Collapse } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTabAlerts } from '../services/tabAlert/useTabAlerts';

const useStyles = makeStyles()((theme) => ({
    alerts: {
        display: 'flex',
        flexDirection: 'column',
        paddingTop: theme.spacing(0.5),
        width: theme.spacing(45),
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: theme.zIndex.tooltip,
    },
    microFront: {
        borderRadius: theme.spacing(1),
        boxShadow: '0 2px 4px hsl(240 3.8% 46.1% / 12%)',
    },
    alert: {
        position: 'relative',
        boxSizing: 'border-box',
        '&:not(:hover) .close': {
                display: 'none',
        },
        /* Hide scrollbar for IE, Edge and Firefox */
        scrollbarWidth: 'none',
        /* Hide scrollbar for Chrome, Safari and Opera */
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    close: {
        position: 'absolute',
        left: theme.spacing(-0.5),
        top: theme.spacing(-0.5),
        width: theme.spacing(2),
        height: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        borderRadius: '50%',
        border: `1px solid white`,
        backgroundColor: theme.palette.common.gray,
        '& svg': {
            fontSize: theme.spacing(1.5),
            color: 'white',
        },
    }
}));

const MicroFrontStartSize = Object.freeze({
    width: '100%',
    height: 0,
});

const Alert = ({ alert, container, onShowAlert }) => {
    const { id, microFront, closeable } = alert;
    const { classes, cx } = useStyles();
    const [ready, setReady] = useState(false);
    const [show, setShow] = useState(false);
    const microFrontHandler = useRef(null);
    const hasPlayedSoundRef = useRef(false);

    useEffect(() => {
        const timeout = setTimeout(() => setShow(ready), 500);
        
        return () => clearTimeout(timeout);
    }, [ready]);
    
    useEffect(() => {
        if (show && !hasPlayedSoundRef.current) {
            hasPlayedSoundRef.current = true;
            onShowAlert?.(alert);
        }
    }, [show, alert, onShowAlert]);
    
    return (
        <Collapse in={show} container={container}>
            <div className={classes.alert}>
                <MicroFront
                    className={classes.microFront}
                    {...microFront}
                    onReady={setReady}
                    skipLoader={true}
                    resizable={true}
                    startSize={MicroFrontStartSize}
                    alert={alert}
                    handlerRef={microFrontHandler}
                />
                { closeable ? (
                    <div className={cx(classes.close, 'close')} onClick={() => microFrontHandler.current?.closeAlert(id)}>
                        <CloseIcon fontSize="small"/>
                    </div>
                ) : null}
            </div>
        </Collapse>
    );
}

export default function VmsAlerts() {
    const { classes } = useStyles();
    const ref = useRef(null);
    
    const { alerts, playContactRequestNotification } = useMicroFrontContext();

    useTabAlerts(alerts);

    const handleShowAlert = useCallback(
        (alert) => {
            if (
                alert.id === 'notification-contact-request:created' ||
                alert.id?.startsWith('notification-contact-request')
            ) {
                playContactRequestNotification?.();
            }
        },
        [playContactRequestNotification],
    );

    return (
        <div className={classes.alerts} ref={ref}>
            {alerts.map((alert) => (
                <Alert
                    key={alert.correlationId}
                    alert={alert}
                    container={ref.current}
                    onShowAlert={handleShowAlert}
                />
            ))}
        </div>
    );
}